// **The projected 16:9 type scale** (`demo-presentation-shell` task 4.1/4.2, requirement "The
// slide deck is in-repo components over the kit tokens"). This is the deck's own candidate scale
// from `prototype/sketches/demo-presentation-shell/slide-chrome.html` — the sketch's `--slide-*`
// custom properties, defined "for the deck alone and never leaking into the app's phone screens".
//
// The sketch previews a 1920x1080 projector slide at a 960x540 (0.5×) panel, so its own
// `--slide-*` values are HALF the real projected size — this module doubles them back to the
// actual pixel sizes the fullscreen `/demo` surface renders at. These are deliberately **not**
// `prototype/tokens/*.css` values (there is no kit "slide" scale — a phone-first `--text-2xl` on a
// projector is exactly the failure panel 4 of the sketch exists to show), so `slides.test.tsx`'s
// hard-coded-token-value check does not (and must not) hold numeric font sizes to any kit token:
// the scenario it binds names colour, radius, shadow and font-*family* only.
//
// Retuned 2026-08-09 against real 1920x1080 screenshots. Two things changed and both were
// legibility, not taste: the old scale had **one** body size (42) doing duty for a diagram
// annotation and a paragraph of prose, and the diagram row at `node: 34` / `edge: 26` was wide
// enough at four parallel branches to wrap into two lines. `caption` splits the small end off
// `body`, and the diagram pair came down so a four-branch row fits one line at 1920.
//
// 2026-08-11: the diagrams themselves are now laid out by Mermaid, which no longer needs a row to be
// hand-fitted. `node` is still the size a graph's node label is set at (`deck-theme.ts` feeds it to
// Mermaid's `themeVariables`) and `node`/`edge` still size the surviving hand-built chips, so the
// retuning above stands — it is just no longer *load-bearing* for whether a branch wraps.
/** The projected surface every value in this module is tuned against. `SlideFrame` lays a slide out
 * at exactly this size and scales the whole box to fit its container, so a laptop window, a 16:10
 * panel or a 4:3 projector all get the *same* layout — same line breaks, same diagram rows — just
 * smaller. Retuning any number below therefore stays a 1920×1080 judgement, as it always was. */
export const SLIDE_FRAME = { width: 1920, height: 1080 } as const;

export const SLIDE_SCALE = {
  hero: 116,
  title: 76,
  quote: 54,
  body: 40,
  caption: 30,
  label: 26,
  node: 34,
  edge: 22,
} as const;

/**
 * **The projected slide's own spacing.** Kit `--sp-*` tops out at 32px, which is a phone's
 * comfortable gutter and a projector's hairline: a 1920-wide slide inset by 32px reads as text
 * pinned to the bezel. These are the deck's own margins for the same reason `SLIDE_SCALE` is the
 * deck's own type scale, and for the same reason they are not kit tokens.
 */
export const SLIDE_SPACE = {
  /** Left/right inset of every slide's content column. */
  padX: 120,
  /** Top/bottom inset. */
  padY: 84,
  /** Between a title and its body, and between stacked blocks within a body. */
  gap: 48,
} as const;
